import React, { useMemo } from 'react';
import { v4 as uuidV4 } from 'uuid';

import { GetEventLogsQueryGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { EventLogExportDataRow } from './types';
import * as CreditFacilitiesEntityConfig from '~/core/routes/credit-facilities/EntityConfig';
import * as DeliverablesEntityConfig from '~/core/routes/deliverables/EntityConfig';
import * as FundingRequestsEntityConfig from '~/core/routes/funding-requests/EntityConfig';
import * as InvoicesEntityConfig from '~/core/routes/invoices/EntityConfig';
import * as LimitedPartnersEntityConfig from '~/core/routes/limited-partners/EntityConfig';
import * as UsersEntityConfig from '~/core/routes/users/EntityConfig';
import * as ValleyBankLoanAccountsEntityConfig from '~/core/routes/valley-bank/EntityConfig';
import { CsvXlsxDownloadSelect } from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';

// Types
type EventLogData = GetEventLogsQueryGQLType['eventLogs']['items'][number];

// Exports
type EventLogExportButtonProps = {
  logs: EventLogData[];
};

export const EventLogExportButton = ({ logs }: EventLogExportButtonProps) => {
  const organizedData = useMemo(() => {
    const headersToShow = new Set<string>();

    const data = logs.map((log) => {
      /* eslint-disable camelcase */
      const dataRow: EventLogExportDataRow = {
        eventId: log.eventId,
        eventLogType: log.eventLogType,
        entityId: log.entityId,
        eventMessage: log.eventMessage,
        createdAt: new Date(log.createdAt).toISOString(),
        updatedAt: new Date(log.updatedAt).toISOString(),
        actor_id: log.actor.id,
        actor_firstName: log.actor.firstName,
        actor_lastName: log.actor.lastName,
        actor_organization_id: log.actor.organization.id,
        actor_organization_name: log.actor.organization.name,
      };
      /* eslint-enable camelcase */

      // Run through each entity's exporter function. These functions add extra data onto
      // an existing row, and return the original row if the log doesn't match. This means
      // we can call them all in sequence to fill in all the necessary export data.
      const dataRowWithEntityData = [
        CreditFacilitiesEntityConfig.toExportDataRow,
        DeliverablesEntityConfig.toExportDataRow,
        FundingRequestsEntityConfig.toExportDataRow,
        LimitedPartnersEntityConfig.toExportDataRow,
        UsersEntityConfig.toExportDataRow,
        ValleyBankLoanAccountsEntityConfig.toExportDataRow,
        InvoicesEntityConfig.toExportDataRow,
      ].reduce(
        (prevDataRow, toExportDataRow) => toExportDataRow(prevDataRow, log),
        dataRow,
      );

      // *** Headers ***
      Object.keys(dataRowWithEntityData).forEach((key) => {
        headersToShow.add(key);
      });

      return dataRowWithEntityData;
    });

    const headers = Array.from(headersToShow).map((key) => ({
      key,
      label: key,
    }));

    return { headers, data };
  }, [logs]);

  return (
    <CsvXlsxDownloadSelect
      headers={organizedData.headers}
      dataRows={organizedData.data}
      filenamePrefix={`event-log-export-${uuidV4().slice(-12)}`}
      linkText="Export"
      MenuButtonProps={{
        colorScheme: 'gray',
        size: 'sm',
        variant: 'secondary-muted',
      }}
    />
  );
};
